import type { GetServerSidePropsContext } from "next";
import type { OrderType } from "@/constants/enums";
import type { BasePageProps } from "@/types";
import { useRouter } from "next/router";
import { useLayoutEffect } from "react";

import MenuScreen from "@/components/menu/MenuScreen";
import { ORDER_TYPE_ENUM } from "@/constants/enums";
import { loadBasePageProps } from "@/lib/pageProps";
import { useAppDispatch, useAppSelector } from "@/store";
import { setOrderType } from "@/store/slices/cartSlice";
import { rememberOrderType } from "@/lib/orderingUi";

const ORDER_TYPE_BY_QUERY: Record<string, OrderType | undefined> = {
  "take-away": ORDER_TYPE_ENUM.TAKE_AWAY,
  takeaway: ORDER_TYPE_ENUM.TAKE_AWAY,
  delivery: ORDER_TYPE_ENUM.DELIVERY,
};

/**
 * `/menu` — the take-away and delivery menu.
 *
 * The page itself is only the order-type effect; everything you can see is
 * `MenuScreen`, which is the original's `MenuBody` + `CartPanel` pair and is
 * shared verbatim with `/menu/dine-in`, exactly as the original shares modules
 * 9802 and 91849 between the two routes.
 */
export default function MenuPage({ data, metadata, baseUrl }: BasePageProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const currentOrderType = useAppSelector((state) => state.cart.orderType);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const queryValue = router.query.order_type;
  const requestedOrderType =
    ORDER_TYPE_BY_QUERY[String(queryValue ?? "").toLowerCase()];

  // `/menu?order_type=delivery` selects the customer fulfilment method. Dine-in
  // deliberately is not accepted on this generic customer route: reservations
  // live on the landing page, while QR/table and staff dine-in use the guarded
  // `/menu/dine-in` route.
  // Do this before paint and do not mount MenuScreen with a stale persisted
  // method. Previously its delivery-address effect could run with DELIVERY
  // from the last visit before this parent changed the query-selected method
  // to TAKE-AWAY, so clicking Takeaway incorrectly asked for a location.
  useLayoutEffect(() => {
    if (requestedOrderType) rememberOrderType(data?.settings?._id, isStaff, requestedOrderType);
    if (requestedOrderType && requestedOrderType !== currentOrderType) {
      dispatch(setOrderType(requestedOrderType));
    }
  }, [currentOrderType, dispatch, requestedOrderType, data?.settings?._id, isStaff]);

  if (requestedOrderType && requestedOrderType !== currentOrderType) return null;

  return (
    <MenuScreen data={data} metadata={metadata} baseUrl={baseUrl} />
  );
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<{ props: BasePageProps }> {
  // The BRANCH is resolved first and both store fetches are made for it, so the
  // server render is already the chosen outlet's. See lib/pageProps.ts.
  return { props: await loadBasePageProps(ctx) };
}
